import { createContext, useState, useCallback, useEffect } from 'react';
import { authService } from '../features/authentication/services/authService';
import { tokenStorage } from '../services/auth/tokenStorage';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const token = tokenStorage.getAccessToken();
      const storedUser = tokenStorage.getUser();
      const storedUsername = tokenStorage.getUsername();
      const storedPermissions = tokenStorage.getPermissions();
      const storedRoles = tokenStorage.getRoles();
      if (token && storedUser) {
        setUser(storedUser);
        setUsername(storedUsername);
        setPermissions(storedPermissions);
        setRoles(storedRoles);
        setIsAuthenticated(true);
      }
    } catch {
      // ignore parse errors
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async ({ username: loginUsername, password }) => {
    const { user: authenticatedUser, accessToken, refreshToken, permissions: userPermissions, roles: userRoles } = await authService.login({
      username: loginUsername,
      password,
    });
    tokenStorage.setAccessToken(accessToken);
    if (refreshToken) tokenStorage.setRefreshToken(refreshToken);
    tokenStorage.setUser(authenticatedUser);
    tokenStorage.setUsername(loginUsername);
    tokenStorage.setPermissions(userPermissions);
    tokenStorage.setRoles(userRoles);
    setUser(authenticatedUser);
    setUsername(loginUsername);
    setPermissions(userPermissions);
    setRoles(userRoles);
    setIsAuthenticated(true);
    return authenticatedUser;
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStorage.getRefreshToken();
    // Clear the local session immediately so the UI updates without waiting for the backend.
    tokenStorage.clear();
    setUser(null);
    setUsername(null);
    setPermissions([]);
    setRoles([]);
    setIsAuthenticated(false);
    // Notify the backend in the background; failures do not block the logout.
    if (refreshToken) {
      try {
        await authService.logout(refreshToken);
      } catch {
        // Ignore backend logout errors; the local session is already gone.
      }
    }
  }, []);

  const changePassword = useCallback(async ({ oldPassword, newPassword }) => {
    const currentUsername = username || tokenStorage.getUsername();
    if (!currentUsername) throw new Error('Missing username in session.');
    return authService.changePassword({
      username: currentUsername,
      oldPassword,
      newPassword,
    });
  }, [username]);

  const hasPermission = useCallback((perm) => {
    return permissions.includes(perm);
  }, [permissions]);

  return (
    <AuthContext.Provider
      value={{ user, username, permissions, roles, isAuthenticated, loading, login, logout, changePassword, hasPermission }}
    >
      {children}
    </AuthContext.Provider>
  );
}
